import React, { useContext, useState } from 'react'
import styled from '@emotion/styled'
import { useLocation } from '@reach/router'

// Import Components
import { LinkGroup } from './LinkGroup'
import { BDCSocialMediaGroup } from '../SocialMedia/BDCSocialMediaGroup'
import { SiteConfigContext } from '../SiteConfigProvider'
import { Link } from '@jelly/ui'

// Styled Components via Emotion
const FooterWrapper = styled.div`
	/** Next rule is needed for old stack */
	a {
		${({ theme }) => theme.fonts.primary};
	}

	background: ${({ theme }) => `linear-gradient(90deg, ${theme.colors.primary} 50%, ${theme.colors.lightGrey} 50%)`};
	width: 100%;
`
const FooterContainer = styled.div`
	background: ${({ theme }) => theme.colors.primary};
	display: flex;
	flex-direction: column;
	margin: auto;
	padding: 40px 40px;

	${({ theme }) => theme.MQ.M} {
		${({ theme }) => theme.maxWidth.L};
		background: ${({ theme }) => theme.colors.lightGrey};
		flex-direction: row;
		padding: 0;
	}
`
const FooterLink = styled(Link)`
	cursor: pointer;
	color: ${({ theme }) => theme.colors.white};
	display: block;
	font-size: 14px;
	width: fit-content;

	&:hover {
		color: ${({ theme }) => theme.navigation.altLinkHoverColor};
	}
`
const DesktopContactUs = styled.div`
	color: ${({ theme }) => theme.colors.white};
	display: block;
	line-height: 30px;
`
const FooterContactData = styled.div`
	color: ${({ theme }) => theme.colors.white};
	font-size: 14px;
`
const CopyrightDesktop = styled.div`
	bottom: 0;
	display: none;
	margin: 10px 0;
	top: 40vh;

	${({ theme }) => theme.MQ.M} {
		display: block;
	}
`
const CopyrightMobile = styled.div`
	display: block;
	margin: 10px 0;

	${({ theme }) => theme.MQ.M} {
		display: none;
		bottom: 0;
		margin-left: auto;
		padding: 0 30px;
		position: absolute;
		right: 30px;
		top: 40vh;
	}
`
const Copyright = styled.div`
	color: ${({ theme }) => theme.colors.white};
	display: block;
	line-height: 20px;

	${({ theme }) => theme.MQ.M} {
		font-size: 14px;
	}
`
const MobileOnly = styled.div`
	${({ theme }) => theme.MQ.M} {
		display: none;
	}
`
const DesktopOnly = styled.div`
	display: none;

	${({ theme }) => theme.MQ.M} {
		display: block;
	}
`

const FooterLinkGroup = styled.div`
	padding-top: 15px;

	${({ theme }) => theme.MQ.M} {
		background: ${({ theme }) => theme.colors.lightGrey};
		bottom: 0;
		display: flex;
		justify-content: space-around;
		flex-basis: 70%;
		padding: 75px 30px 20px 30px;
		position: relative;
		margin: auto 0 40px 0;
	}

	${({ theme }) => theme.MQ.XL} {
		padding: 75px 10px 20px 10px;
	}
`

const FooterContentGroup = styled.div`
	${({ theme }) => theme.MQ.M} {
		background: ${({ theme }) => theme.colors.primary};
		flex-basis: 30%;
		padding: 75px 30px 20px 30px;
	}
`

/**
 * This Component is the Footer View
 */
export function FooterView() {
	const {
		siteConstants: {
			FOOTER_LINKS: { resources, ourCompany, ourBrands, contactUs },
		},
		siteComponents: { FooterLogo },
	} = useContext(SiteConfigContext)

	const [activeCategory, setVisibleCategory] = useState(false)
	const { pathname } = useLocation()

	const resourceLinks = resources.map((resource) => (
		<FooterLink key={resource.text} href={resource.link}>
			{resource.text}
		</FooterLink>
	))
	const ourCompanyLinks = ourCompany.map(({ link, target, text }) => (
		<FooterLink key={text} href={link} target={target}>
			{text}
		</FooterLink>
	))
	const ourBrandsLinks = ourBrands.map(({ link, target, text }) => (
		<FooterLink key={text} href={link} target={target}>
			{text}
		</FooterLink>
	))

	const contactUsLinks = contactUs.map((resource) => {
		if (resource.dataType === 'link') {
			if (pathname === '/connect/') {
				if (resource.type === 'email') {
					return (
						<FooterLink key="sales@business.com" href="mailto:sales@business.com">
							sales@business.com
						</FooterLink>
					)
				} else if (resource.type === 'phone') {
					return (
						<FooterLink key="1-888-393-5000 x4" href="tel:1-888-393-5000;ext=4">
							888.393.5000 x4
						</FooterLink>
					)
				}
			}
			return (
				<FooterLink key={resource.text} href={resource.link}>
					{resource.text}
				</FooterLink>
			)
		} else {
			return <FooterContactData key={resource.text}>{resource.text}</FooterContactData>
		}
	})

	const handleCategoryClick = (category) => {
		if (activeCategory === category) {
			// set to null to close if already selected
			setVisibleCategory(null)
		} else {
			setVisibleCategory(category)
		}
	}

	const year = new Date().getFullYear()
	return (
		<FooterWrapper>
			<FooterContainer>
				<FooterContentGroup>
					<FooterLogo />
					<DesktopOnly>
						<DesktopContactUs>{contactUsLinks}</DesktopContactUs>

						<BDCSocialMediaGroup variant="inverse" />

						{/* Can think about refactoring the desktop and mobile copyright into one */}
						<CopyrightDesktop>
							<Copyright>&copy; {year} business.com</Copyright>
							<Copyright>All Rights Reserved.</Copyright>
						</CopyrightDesktop>
					</DesktopOnly>
				</FooterContentGroup>
				<FooterLinkGroup>
					<LinkGroup
						activeCategory={activeCategory}
						categoryId="resources"
						categoryText="Resources"
						handleCategoryClick={handleCategoryClick}
						links={resourceLinks}
					/>
					<LinkGroup
						activeCategory={activeCategory}
						categoryId="ourCompany"
						categoryText="Our Company"
						handleCategoryClick={handleCategoryClick}
						links={ourCompanyLinks}
					/>
					<LinkGroup
						activeCategory={activeCategory}
						categoryId="ourBrands"
						categoryText="Our Brands"
						handleCategoryClick={handleCategoryClick}
						links={ourBrandsLinks}
					/>
					<MobileOnly>
						<LinkGroup
							activeCategory={activeCategory}
							categoryId="contactUs"
							categoryText="Contact Us"
							handleCategoryClick={handleCategoryClick}
							links={contactUsLinks}
						/>
						<BDCSocialMediaGroup variant="inverse" />
					</MobileOnly>
				</FooterLinkGroup>
				<CopyrightMobile>
					<Copyright>&copy; {year} business.com</Copyright>
					<Copyright>All Rights Reserved.</Copyright>
				</CopyrightMobile>
			</FooterContainer>
		</FooterWrapper>
	)
}
